import { useSyncExternalStore, useCallback } from 'react';
import {
  readConsent,
  writeConsent,
  isCategoryAllowed,
  COOKIE_CONSENT_CHANGE_EVENT,
  PENDING_CONSENT,
  type ConsentChoice,
  type ConsentCategory,
} from '@sighthost/config';

const subscribe = (onStoreChange: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener('storage', onStoreChange);
  window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, onStoreChange);
  return () => {
    window.removeEventListener('storage', onStoreChange);
    window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT, onStoreChange);
  };
};

const getServerSnapshot = (): ConsentChoice => PENDING_CONSENT;

export interface UseCookieConsentReturn {
  /** Current persisted choice; `{ status: 'pending' }` until user picks. */
  consent: ConsentChoice;
  /** Persist a new choice and broadcast to subscribers. */
  set: (choice: ConsentChoice) => void;
  /** Clear the persisted choice — useful for "Manage your choice" buttons. */
  reopen: () => void;
  /** Convenience: `true` if analytics/marketing scripts can mount. */
  allows: (category: ConsentCategory) => boolean;
}

/**
 * React hook around the cookie-consent state machine in `@sighthost/config`.
 * Re-renders on cross-tab changes via `storage` event and same-tab changes
 * via `afrilink:cookie-consent-change` custom event.
 */
export function useCookieConsent(): UseCookieConsentReturn {
  const consent = useSyncExternalStore(subscribe, readConsent, getServerSnapshot);
  const set = useCallback((choice: ConsentChoice) => writeConsent(choice), []);
  const reopen = useCallback(() => writeConsent(PENDING_CONSENT), []);
  const allows = useCallback(
    (category: ConsentCategory) => isCategoryAllowed(consent, category),
    [consent],
  );
  return { consent, set, reopen, allows };
}
